<template>
  <div class="station-form-category-container">
    <span class="station-form-category-title">Horário e data</span>
    <div class="station-form-category-content">
      <v-row dense>
        <v-col cols="12" md="3">
          <DatePicker
            @update:model-value="setDate"
            :model-value="startDateModel"
            locale="pt-BR"
            :masks="{ input: `DD/MM/YYYY` }"
            :max-date="scheduleEndDate ? new Date(scheduleEndDate) : null"
            :initial-page="{ month: new Date().getMonth() + 1, year: new Date().getFullYear() }"
            :update-on-input="false"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                data-testid="station-start-date-input"
                :model-value="inputValue"
                v-on="guardDateInput(inputEvents)"
                label="Data de Início"
                :append-inner-icon="mdiCalendarBlankOutline"
                color="primary20"
                variant="outlined"
                density="comfortable"
                :error-messages="dateErr"
              />
            </template>
          </DatePicker>
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field
            :append-inner-icon="mdiClockOutline"
            @update:model-value="setStartHour"
            :model-value="startHour"
            v-maska="'##:##'"
            label="Hora de Início"
            color="primary20"
            variant="outlined"
            density="comfortable"
            :error-messages="startHourErr"
          />
        </v-col>

        <v-col cols="12" md="3">
          <DatePicker
            @update:model-value="setEndDate"
            locale="pt-BR"
            :model-value="endDateModel"
            :masks="{ input: `DD/MM/YYYY` }"
            :max-date="scheduleEndDate ? new Date(scheduleEndDate) : null"
            :initial-page="{ month: new Date().getMonth() + 1, year: new Date().getFullYear() }"
            :update-on-input="false"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                data-testid="station-end-date-input"
                :model-value="inputValue"
                v-on="guardDateInput(inputEvents)"
                label="Data de Fim"
                :append-inner-icon="mdiCalendarBlankOutline"
                color="primary20"
                variant="outlined"
                density="comfortable"
                :error-messages="endDateErr"
              />
            </template>
          </DatePicker>
        </v-col>

        <v-col cols="12" md="3">
          <v-text-field
            :append-inner-icon="mdiClockOutline"
            @update:model-value="setEndHour"
            :model-value="endHour"
            v-maska="'##:##'"
            label="Hora de Fim"
            color="primary20"
            variant="outlined"
            density="comfortable"
            :error-messages="endHourErr"
          />
        </v-col>

        <v-col cols="12" md="12" class="mt-4">
          <v-autocomplete
            @update:model-value="setTimezone"
            :items="timezones"
            :model-value="timezone"
            color="primary20"
            label="Fuso Horário"
            variant="outlined"
            density="comfortable"
            :auto-select-first="true"
            class="!w-full"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item
                v-bind="props"
                :title="item?.raw?.title"
                :subtitle="item?.raw?.subtitle"
              />
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <!-- Feedback de Duração e Alerta FatFinger -->
      <div v-if="calculatedDuration" class="mt-3 transition-all duration-300">
        <div
          class="flex items-center text-sm font-bold"
          :class="calculatedDuration.isOver24h ? 'text-red-600' : 'text-gray-600'"
        >
          <v-icon
            v-if="calculatedDuration.isOver24h"
            :icon="mdiAlertCircle"
            size="small"
            class="mr-1"
          />
          Duração calculada: {{ calculatedDuration.text }}
        </div>

        <div
          v-if="calculatedDuration.isOver24h"
          class="mt-2 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-red-700 text-sm"
        >
          <v-icon :icon="mdiAlertCircle" size="small" class="mt-0.5" />
          <div class="flex flex-col text-left">
            <span class="font-bold">Duração excede o limite permitido</span>
            <span
              >A duração do plantão não pode ser superior a 24 horas. Por favor, verifique as datas
              e horários inseridos.</span
            >
          </div>
        </div>
      </div>

      <!-- Mensagem de erro visual local (Outros erros) -->
      <div
        v-if="
          validationError &&
          validationError !== 'A duração do plantão não pode ser superior a 24 horas.'
        "
        class="text-red-600 text-sm mt-2 font-medium"
      >
        {{ validationError !== 'INCOMPLETE' ? validationError : '' }}
      </div>

      <div v-if="!isUpdateMode">
        <span class="station-form-category-title text-left block mb-2 mt-4">Recorrência</span>
        <div class="station-form-category-columns justify-start mb-3">
          <v-radio-group
            @update:model-value="setConfigurationRepetition"
            :model-value="configurationRepetition"
            inline
            class="flex justify-start"
          >
            <v-radio label="Não se repete" color="primary" value="no-repeat"></v-radio>
            <v-radio label="Repetir" color="primary" value="daily"></v-radio>
            <!-- <v-radio label="Personalizado" color="primary" value="custom"></v-radio> -->
          </v-radio-group>
        </div>

        <v-row dense v-if="configurationRepetition === 'no-repeat'">
          <v-col cols="5">
            <!-- FAZER INTEGRAÇÃO DA SELEÇÃO DE POSITIONS -->
            <!-- <select-positions
              :key="`sposition-${schedule || 0}`"
              :schedule-id="schedule"
              v-model="positionsByRowStations[i]"
              @onSelect="($e) => onSelectPositionNoRepeat($e, i)"
            /> -->
          </v-col>
          <v-col cols="7">
            <DatePicker
              @update:model-value="setFinishedAtUntil"
              :model-value="finishedAtUntil"
              locale="pt-BR"
              :masks="{ input: `DD MMM YYYY` }"
              :initial-page="{ month: new Date().getMonth() + 1, year: new Date().getFullYear() }"
            >
              <template #default="{ inputValue, inputEvents }">
                <v-text-field
                  v-if="finishedAt === 'until'"
                  placeholder="Range"
                  :prepend-inner-icon="mdiCalendarBlankOutline"
                  :value="inputValue"
                  v-on="inputEvents"
                  density="comfortable"
                  class="w-full"
                  color="primary20"
                ></v-text-field>
              </template>
            </DatePicker>
            <v-text-field
              v-if="finishedAt === 'occurrence'"
              @update:model-value="setFinishedAtAfter"
              :model-value="finishedAtAfter"
              type="number"
              density="comfortable"
              placeholder="Nº de ocorrências"
              class="w-full"
              color="primary20"
              suffix="Ocorrencias"
            />
          </v-col>
        </v-row>
        <!-- <v-row dense v-if="configurationRepetition === 'custom'">
          <v-col cols="12">
            <v-autocomplete
              class="w-full"
              @update:model-value="setValueRepetition"
              :model-value="valueRepetition"
              :items="optionsRepeatStation"
              color="primary20"
              :disabled="String(configurationRepetition) !== 'custom'"
              density="comfortable"
              label="Repetições"
              multiple
              chips
              :error-messages="repetitionErr"
            />
          </v-col>
        </v-row> -->
        <div v-if="configurationRepetition !== 'no-repeat'">
          <v-row class="gap-2 w-100">
            <v-row no-gutters class="flex-nowrap gap-2 min-h-36">
              <v-col class="stations-cols position-col">
                <v-card class="card-position mb-2" title="Posição" disabled />
                <v-card
                  v-for="(v, i) in new Array(biggerCountFromWeekDays)"
                  :key="'position-' + i"
                  class="card-position mb-2"
                >
                  <select-positions
                    class="select-position-bulk ml-2"
                    :key="`sposition-${schedule || 0}`"
                    :schedule-id="schedule"
                    v-model="positionsByRowStations[i]"
                    @onSelect="($e) => onSelectPosition($e, i)"
                    variant="plain"
                  />
                </v-card>
              </v-col>

              <v-col class="stations-cols">
                <v-card
                  @click="addStationInWeekDay('segunda')"
                  class="card-station card-header"
                  title="Segunda"
                  link
                >
                  <template #subtitle>
                    <v-icon :icon="mdiPlus" small color="black" />
                  </template>
                </v-card>
                <v-card
                  v-for="(v, i) in new Array(stationsCountByWeekDays.segunda)"
                  :key="'segunda-' + i"
                  @click="removeStationInWeekDay('segunda')"
                  class="card-station card-new-station"
                  link
                >
                  <v-icon :icon="mdiClose" size="x-small" color="black" class="vaga-icon-close" />
                  Vaga
                </v-card>
              </v-col>

              <v-col class="stations-cols">
                <v-card
                  @click="addStationInWeekDay('terca')"
                  class="card-station card-header"
                  title="Terça"
                  link
                >
                  <template #subtitle>
                    <v-icon :icon="mdiPlus" small color="black" />
                  </template>
                </v-card>
                <v-card
                  v-for="(v, i) in new Array(stationsCountByWeekDays.terca)"
                  :key="'terca-' + i"
                  @click="removeStationInWeekDay('terca')"
                  class="card-station card-new-station"
                  link
                >
                  <v-icon :icon="mdiClose" size="x-small" color="black" class="vaga-icon-close" />
                  Vaga
                </v-card>
              </v-col>

              <v-col class="stations-cols">
                <v-card
                  @click="addStationInWeekDay('quarta')"
                  class="card-station card-header"
                  title="Quarta"
                  link
                >
                  <template #subtitle> <v-icon :icon="mdiPlus" small color="black" /> </template>
                </v-card>
                <v-card
                  v-for="(v, i) in new Array(stationsCountByWeekDays.quarta)"
                  :key="'quarta-' + i"
                  @click="removeStationInWeekDay('quarta')"
                  class="card-station card-new-station"
                  link
                >
                  <v-icon :icon="mdiClose" size="x-small" color="black" class="vaga-icon-close" />
                  Vaga
                </v-card>
              </v-col>

              <v-col class="stations-cols">
                <v-card
                  @click="addStationInWeekDay('quinta')"
                  class="card-station card-header"
                  title="Quinta"
                  link
                >
                  <template #subtitle> <v-icon :icon="mdiPlus" small color="black" /> </template>
                </v-card>
                <v-card
                  v-for="(v, i) in new Array(stationsCountByWeekDays.quinta)"
                  :key="'quinta-' + i"
                  @click="removeStationInWeekDay('quinta')"
                  class="card-station card-new-station"
                  link
                >
                  <v-icon :icon="mdiClose" size="x-small" color="black" class="vaga-icon-close" />
                  Vaga
                </v-card>
              </v-col>

              <v-col class="stations-cols">
                <v-card
                  @click="addStationInWeekDay('sexta')"
                  class="card-station card-header"
                  title="Sexta"
                  link
                >
                  <template #subtitle> <v-icon :icon="mdiPlus" small color="black" /> </template>
                </v-card>
                <v-card
                  v-for="(v, i) in new Array(stationsCountByWeekDays.sexta)"
                  :key="'sexta-' + i"
                  @click="removeStationInWeekDay('sexta')"
                  class="card-station card-new-station"
                  link
                >
                  <v-icon :icon="mdiClose" size="x-small" color="black" class="vaga-icon-close" />
                  Vaga
                </v-card>
              </v-col>

              <v-col class="stations-cols">
                <v-card
                  @click="addStationInWeekDay('sabado')"
                  class="card-station card-header"
                  title="Sábado"
                  link
                >
                  <template #subtitle> <v-icon :icon="mdiPlus" small color="black" /> </template>
                </v-card>
                <v-card
                  v-for="(v, i) in new Array(stationsCountByWeekDays.sabado)"
                  :key="'sabado-' + i"
                  @click="removeStationInWeekDay('sabado')"
                  class="card-station card-new-station"
                  link
                >
                  <v-icon :icon="mdiClose" size="x-small" color="black" class="vaga-icon-close" />
                  Vaga
                </v-card>
              </v-col>

              <v-col class="stations-cols">
                <v-card
                  @click="addStationInWeekDay('domingo')"
                  class="card-station card-header"
                  title="Domingo"
                  link
                >
                  <template #subtitle> <v-icon :icon="mdiPlus" small color="black" /> </template>
                </v-card>
                <v-card
                  v-for="(v, i) in new Array(stationsCountByWeekDays.domingo)"
                  :key="'domingo-' + i"
                  @click="removeStationInWeekDay('domingo')"
                  class="card-station card-new-station"
                  link
                >
                  <v-icon :icon="mdiClose" size="x-small" color="black" class="vaga-icon-close" />
                  Vaga
                </v-card>
              </v-col>
            </v-row>
          </v-row>
        </div>

        <v-row dense v-if="configurationRepetition !== 'no-repeat'">
          <v-col cols="5">
            <v-select
              label="Repetir"
              @update:model-value="setFinishedAt"
              :items="finishedAtOptions"
              :model-value="finishedAt"
              color="primary20"
              density="comfortable"
            />
            <p class="text-sm text-[#68688D] mt-[-12px]">
              Final configurado da escala: <b> {{ formattedScheduleEndDate }}</b>
            </p>
          </v-col>
          <v-col cols="7">
            <DatePicker
              @update:model-value="setFinishedAtUntil"
              :model-value="finishedAtUntil"
              locale="pt-BR"
              :masks="{ input: `DD MMM YYYY` }"
              :max-date="scheduleEndDate ? new Date(scheduleEndDate) : null"
              :initial-page="{ month: new Date().getMonth() + 1, year: new Date().getFullYear() }"
            >
              <template #default="{ inputValue, inputEvents }">
                <v-text-field
                  v-if="finishedAt === 'until'"
                  placeholder="Range"
                  :append-inner-icon="mdiCalendarBlankOutline"
                  :value="inputValue"
                  v-on="inputEvents"
                  density="comfortable"
                  class="w-full"
                  color="primary20"
                ></v-text-field>
              </template>
            </DatePicker>
            <v-text-field
              v-if="finishedAt === 'occurrence'"
              @update:model-value="setFinishedAtAfter"
              :model-value="finishedAtAfter"
              type="number"
              density="comfortable"
              placeholder="Nº de ocorrências"
              class="w-full"
              color="primary20"
              suffix="Ocorrencias"
            />
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { reactive, computed, watch } from 'vue'
import { DatePicker } from 'v-calendar'
import {
  mdiCalendarBlankOutline,
  mdiClockOutline,
  mdiPlus,
  mdiClose,
  mdiAlertCircle
} from '@mdi/js'
import { timezones } from '@/hooks/useCommon'
import SelectPositions from './select.positions.vue'
import { ref } from 'vue'
import { format, differenceInMinutes, parse } from 'date-fns'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { finishedAtOptions } from '../../helpers'

const props = defineProps([
  'isUpdateMode',
  'date',
  'endDate',
  'timezone',
  'startHour',
  'endHour',
  'configurationRepetition',
  'valueRepetition',
  'repetitionDays',
  'finishedAt',
  'finishedAtUntil',
  'finishedAtAfter',
  'repetitionErr',
  'schedule',
  'scheduleEndDate',
  'dateErr',
  'startHourErr',
  'endHourErr',
  'endDateErr'
])
const emit = defineEmits([
  'update:date',
  'update:endDate',
  'update:timezone',
  'update:startHour',
  'update:endHour',
  'update:configurationRepetition',
  'update:valueRepetition',
  'update:repetitionDays',
  'update:finishedAt',
  'update:finishedAtUntil',
  'update:finishedAtAfter',
  'update:positionsByRowStations',
  'validation-status'
])

enum WeekDaysNumbers {
  'segunda' = '1',
  'terca' = '2',
  'quarta' = '3',
  'quinta' = '4',
  'sexta' = '5',
  'sabado' = '6',
  'domingo' = '0'
}

const stationsCountByWeekDays = reactive({
  segunda: 0,
  terca: 0,
  quarta: 0,
  quinta: 0,
  sexta: 0,
  sabado: 0,
  domingo: 0
})

const parseDateTime = (dateStr: string, timeStr: string): Date | null => {
  if (!dateStr || !timeStr) return null

  try {
    const dateTimeString = `${dateStr} ${timeStr}`
    const parsedDate = parse(dateTimeString, 'dd/MM/yyyy HH:mm', new Date())

    return isNaN(parsedDate.getTime()) ? null : parsedDate
  } catch (e) {
    return null
  }
}

const startDateTime = computed(() => parseDateTime(props.date, props.startHour))
const endDateTime = computed(() => parseDateTime(props.endDate, props.endHour))

const areAllFieldsPresent = computed(() => {
  return !!(props.startHour && props.endHour && props.date && props.endDate)
})

const isFormatValid = computed(() => {
  if (!areAllFieldsPresent.value) return true
  return !!(startDateTime.value && endDateTime.value)
})

const isIntervalValid = computed(() => {
  if (!isFormatValid.value) return true
  if (!startDateTime.value || !endDateTime.value) return true
  return endDateTime.value > startDateTime.value
})

const calculatedDuration = computed(() => {
  if (!startDateTime.value || !endDateTime.value) return null
  if (!isIntervalValid.value) return null

  const totalMinutes = differenceInMinutes(endDateTime.value, startDateTime.value)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  const MS_IN_24_HOURS = 24 * 60 * 60 * 1000
  const msDiff = endDateTime.value.getTime() - startDateTime.value.getTime()
  const isOver24h = msDiff > MS_IN_24_HOURS

  const formattedMinutes = minutes > 0 ? `${minutes}m` : ''
  const formattedDuration = `${hours}h ${formattedMinutes}`.trim()

  return {
    text: formattedDuration,
    isOver24h
  }
})

const validationError = computed((): string | null => {
  if (!props.isUpdateMode && !areAllFieldsPresent.value) {
    return 'INCOMPLETE'
  }

  if (areAllFieldsPresent.value) {
    if (!isFormatValid.value) {
      return 'Formato de data ou hora inválido.'
    }
    if (!isIntervalValid.value) {
      return 'O horário final não pode ser anterior ou igual ao horário inicial.'
    }

    // Regra FatFinger: Duração máxima de 24 horas
    if (calculatedDuration.value?.isOver24h) {
      return 'A duração do plantão não pode ser superior a 24 horas.'
    }
  }

  return null
})

watch(
  validationError,
  (currentError) => {
    const isValid = currentError === null

    emit('validation-status', {
      isValid: isValid,
      message: currentError === 'INCOMPLETE' ? null : currentError
    })
  },
  {
    immediate: true
  }
)

const positionsByRowStations = ref<Array<number | null>>([])

const biggerCountFromWeekDays = computed(() => {
  return Math.max(...Object.values(stationsCountByWeekDays))
})

const DATE_FORMAT = 'dd/MM/yyyy'

const formattedScheduleEndDate = computed(() => {
  if (props.scheduleEndDate) {
    try {
      return format(new Date(props.scheduleEndDate), DATE_FORMAT)
    } catch (e) {
      return 'Data inválida'
    }
  }
  return 'Não definida'
})

/**
 * Só `dd/MM/yyyy` completo conta como data digitada. `parse` do date-fns sozinho não
 * basta: ele aceita `5/10/26` e devolve o ano 26.
 */
const STRICT_DATE_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/

const isTypedDateComplete = (text: string) =>
  STRICT_DATE_PATTERN.test(text) && !isNaN(parse(text, DATE_FORMAT, new Date()).getTime())

/**
 * `'dd/MM/yyyy'` -> `Date`, e qualquer outra coisa -> `null`.
 *
 * Substitui o `new Date(str.split('/').reverse().join('-') + 'T12:00:00')`, que devolvia
 * `Invalid Date` para texto parcial. Esse `Invalid Date` voltava como `model-value` do
 * picker, o v-calendar zerava o `inputValues` e o campo esvaziava na tela enquanto o
 * estado do pai seguia preenchido.
 */
const toPickerDate = (value?: string | null): Date | null => {
  if (!value) return null
  const parsed = parse(value, DATE_FORMAT, new Date())
  return isNaN(parsed.getTime()) ? null : parsed
}

const startDateModel = computed(() => toPickerDate(props.date))
const endDateModel = computed(() => toPickerDate(props.endDate))

type DateInputEvents = Record<string, (event: Event) => void>

/**
 * O `parseDate` do v-calendar termina em `|| new Date(dateString)` (dist/es/index.js:4414):
 * quando a máscara `DD/MM/YYYY` não casa, ele cai no parser leniente do V8, que inventa uma
 * data a partir de texto parcial — `new Date('6')` devolve 01/06/2001. Como o commit do
 * valor acontece no `change` (o `update-on-input` está desligado), basta limpar o texto
 * incompleto antes de entregá-lo à lib.
 */
const guardDateInput = (events: DateInputEvents): DateInputEvents => ({
  ...events,
  change: (event: Event) => {
    const input = event.currentTarget as HTMLInputElement
    const text = input.value.trim()
    if (text && !isTypedDateComplete(text)) {
      input.value = ''
      // O `<v-text-field>` guarda o texto digitado internamente; sem re-emitir `input`
      // ele continuaria exibindo o valor descartado, que é a mesma dessincronia
      // tela/estado que este fix resolve.
      input.dispatchEvent(new Event('input'))
    }
    events.change?.(event)
  }
})

/**
 * Único ponto de escrita das datas no estado do pai: só o `<DatePicker>` emite, e sempre
 * como `Date | null`. `null` (campo apagado ou texto inválido no blur) zera o estado nos
 * dois campos — antes `setDate` fazia `if (!v) return` e retinha no pai uma data que já
 * não estava mais na tela.
 */
const emitDate = (event: 'update:date' | 'update:endDate', value: Date | null) => {
  if (!value || isNaN(value.getTime())) {
    emit(event, '')
    return
  }
  // Sem `value.setHours(12, 0, 0, 0)`: `value` é o `dateValue` interno do v-calendar, e a
  // mutação corrompia o estado da lib. `format` só lê a parte de data.
  emit(event, format(value, DATE_FORMAT))
}

const setDate = (v: Date | null) => emitDate('update:date', v)
const setEndDate = (v: Date | null) => emitDate('update:endDate', v)

const setTimezone = (v: string) => emit('update:timezone', v)
const setStartHour = (v: string) => emit('update:startHour', v)
const setEndHour = (v: string) => emit('update:endHour', v)
const setConfigurationRepetition = (v: string) => {
  return emit('update:configurationRepetition', v)
}

const setFinishedAt = (v: string) => {
  emit('update:finishedAt', v)
}

const setFinishedAtUntil = (v: Date | string) => {
  if (!v) return
  const formattedDate =
    typeof v === 'string' ? v : format(new Date(v.setHours(12, 0, 0, 0)), DATE_FORMAT)
  emit('update:finishedAtUntil', formattedDate)
}
const setFinishedAtAfter = (v: string) => emit('update:finishedAtAfter', v)

function buildRepetionDays(weekDays: typeof stationsCountByWeekDays): Record<string, string> {
  return Object.keys(weekDays).reduce(
    (acc, day) => {
      const dayNumber = WeekDaysNumbers[day as keyof typeof WeekDaysNumbers]
      acc[dayNumber] = String(weekDays[day as keyof typeof weekDays])
      return acc
    },
    {} as Record<string, string>
  )
}

const setRepetitionDays = () => {
  const repetionDays = buildRepetionDays(stationsCountByWeekDays)
  emit('update:repetitionDays', repetionDays)
}

const addStationInWeekDay = (weekDay) => {
  stationsCountByWeekDays[weekDay] += 1
  setRepetitionDays()
}

const removeStationInWeekDay = (weekDay) => {
  stationsCountByWeekDays[weekDay] -= 1
  positionsByRowStations.value.pop()
  // positionsByRowStations.value = popped
  setRepetitionDays()
}

const onSelectPosition = (e, rowIndex) => {
  //adicionar no objeto o ID da position pra vincular na vaga.
  positionsByRowStations.value[rowIndex] = e
  setRepetitionDays()
  emit('update:positionsByRowStations', positionsByRowStations.value)
}

// const onSelectPositionNoRepeat = (e, rowIndex) => {
//   positionsByRowStations.value[rowIndex] = e
//   //adicionar no objeto o ID da position pra vincular na vaga.
//   emit('update:positionsByRowStations', [e])
// }
</script>
<style lang="scss" scoped>
.card-header {
  background-color: #ffffff;
  margin-bottom: 0.5rem;
}

.card-new-station {
  background-color: #ffffff;
  margin-bottom: 0.5rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
}

.vaga-icon-close {
  position: absolute;
  top: 4px;
  right: 4px;
}

.card-position {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  width: 140px;
  height: 56px;
}

.card-station {
  width: 88px;
  height: 56px;
  text-align: center;
}

.card-header :deep(.v-card-title) {
  font-weight: 700;
}

.card-station :deep(.v-card-title),
.card-position :deep(.v-card-title) {
  font-size: 0.8rem;
  padding-top: 0.25rem;
  line-height: 1.5rem;
  text-align: center;
}

.card-station :deep(.v-card-subtitle),
.card-position :deep(.v-card-subtitle) {
  font-size: 0.8rem;
  line-height: 1rem;
  text-align: center;
}

.card-position :deep(.v-card-item) {
  padding: 0 0.6rem;
}

.card-station :deep(.v-card-item) {
  padding: 0 0.6rem;
}

.stations-cols {
  flex: 1 1 0;
  min-width: 88px;
}
.position-col {
  flex: 0 0 140px;
}

:deep(.w-full.select-position-bulk > div > div > div > div.v-field__field > label) {
  font-size: 0.8rem;
}
:deep(div.w-full.select-position-bulk > div > div > div > div.v-field__field > div > input) {
  font-size: 0.8rem;
}
:deep(div.w-full.select-position-bulk > div > div > div > div.v-field__append-inner) {
  font-size: 0.8rem;
}
:deep(
  div.w-full.select-position-bulk.ml-2 > div > div > div > div.v-field__field > div > div > span
) {
  font-size: 0.8rem;
}
</style>
